import React, { useState, useEffect } from 'react';
import api from '../api';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { FiEye, FiUsers, FiMonitor, FiGlobe } from 'react-icons/fi';

const COLORS = ['#d4a843', '#e8c469', '#b8860b', '#8b6914', '#c49b2f'];

const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await api.get('/analytics/dashboard');
      setData(res.data);
    } catch (err) {
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="spinner"></div>;
  if (!data) return <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
    <p style={{ color: 'var(--text-muted)' }}>No analytics data available yet. Visitors will be tracked automatically.</p>
  </div>;

  const { overview, dailyViews, topPages, browsers, os, devices } = data;

  return (
    <div>
      <div className="admin-page-header">
        <h1>Analytics</h1>
      </div>

      {/* Overview Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FiEye size={18} color="var(--accent-primary)" />
            <span className="stat-label">Total Views</span>
          </div>
          <div className="stat-value">{overview.totalViews}</div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FiUsers size={18} color="var(--accent-primary)" />
            <span className="stat-label">Unique Visitors</span>
          </div>
          <div className="stat-value">{overview.totalUnique}</div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FiMonitor size={18} color="var(--accent-primary)" />
            <span className="stat-label">Today</span>
          </div>
          <div className="stat-value">{overview.todayViews}</div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FiGlobe size={18} color="var(--accent-primary)" />
            <span className="stat-label">This Month</span>
          </div>
          <div className="stat-value">{overview.monthViews}</div>
        </div>
      </div>

      {/* Daily Views Chart */}
      {dailyViews?.length > 0 && (
        <div className="chart-container">
          <h3>Daily Visitors (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyViews}>
              <defs>
                <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#d4a843" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#d4a843" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12}
                tickFormatter={(v) => new Date(v).toLocaleDateString('en', { month: 'short', day: 'numeric' })} />
              <YAxis stroke="var(--text-muted)" fontSize={12} />
              <Tooltip
                contentStyle={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)'
                }}
              />
              <Area type="monotone" dataKey="views" stroke="#d4a843" fillOpacity={1} fill="url(#colorViews)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Browser Distribution */}
        {browsers?.length > 0 && (
          <div className="chart-container">
            <h3>Browsers</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={browsers} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis type="number" stroke="var(--text-muted)" fontSize={12} />
                <YAxis type="category" dataKey="browser" stroke="var(--text-muted)" fontSize={12} width={100} />
                <Tooltip
                  contentStyle={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    color: 'var(--text-primary)'
                  }}
                />
                <Bar dataKey="count" fill="#d4a843" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Device Distribution */}
        {devices?.length > 0 && (
          <div className="chart-container">
            <h3>Devices</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={devices}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  dataKey="count"
                  nameKey="device"
                  label={({ device, percent }) => `${device} (${(percent * 100).toFixed(0)}%)`}
                >
                  {devices.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    color: 'var(--text-primary)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Top Pages */}
      {topPages?.length > 0 && (
        <div className="chart-container" style={{ marginTop: '24px' }}>
          <h3>Top Pages</h3>
          <table className="admin-table" style={{ marginTop: '12px' }}>
            <thead>
              <tr>
                <th>Page</th>
                <th>Views</th>
              </tr>
            </thead>
            <tbody>
              {topPages.map((p, i) => (
                <tr key={i}>
                  <td>{p.page}</td>
                  <td>{p.views}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;
